import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Placement } from '@popperjs/core';
import styled from 'styled-components';
import { Lightbulb24, Diamond16 } from '@honeybook/icons';
import { hideInPrint } from '../../styles/mixins';
import { vars } from '../../theme';
import HBPopover from '../HBPopover/HBPopover';
import { TTooltipTheme } from '../HBTooltip/HBTooltip.types';
import { IOnOpenChange, TOpenTrigger } from '../HBPopover/HBPopover.types';

export interface ITooltipProps {
    /** Automatically dismisses the tooltip after the specified milliseconds; disables the hover trigger when set. */
    autoDismissMs?: number;
    /** Content rendered inside the tooltip bubble. */
    children?: ReactNode | ReactNode[];
    /** Additional class name applied to the popover root. */
    className?: string;
    /** Inline styles applied to the popover root. */
    customStyle?: React.CSSProperties;
    /** Delays showing the tooltip by the specified milliseconds after the trigger fires. */
    delayShowInMs?: number;
    /** When true, the tooltip stays open while the cursor hovers over the tooltip content itself. */
    keepOpenOnHover?: boolean;
    /** When true, renders the target element without a tooltip wrapper — no popover is mounted. */
    disabled?: boolean;
    /** Programmatically closes the tooltip; once set to true, overrides all other open logic. */
    forceClose?: boolean;
    /** Programmatically opens the tooltip regardless of hover/focus state. Ignored when `forceClose` is also true. */
    forceOpen?: boolean;
    /** Maximum width of the tooltip bubble. Defaults to '300px'. */
    maxWidth?: string;
    /** Horizontal offset in pixels from the default Popper position. */
    offsetX?: number;
    /** Vertical offset in pixels from the default Popper position. */
    offsetY?: number;
    /** Called whenever the tooltip opens or closes, with the new state and what triggered it. */
    onOpenChange?: ({ isOpen, triggeredFrom }: IOnOpenChange) => void;
    /** Interaction that opens the tooltip; nullified automatically when autoDismissMs is set. */
    openTrigger?: TOpenTrigger;
    /** Popper.js placement for the tooltip bubble relative to the target. Defaults to 'bottom'. */
    placement?: Placement;
    /** When true, clicking outside the tooltip will close it; defaults to false. */
    shouldSupportClickOutside?: boolean;
    /** When true, the wrapper element around the target is focusable for keyboard accessibility. */
    isTargetContainerFocusable?: boolean;
    /**
     * Accessible name for the target wrapper, applied as `aria-label`. Set this
     * when `target` is a bare icon with no accessible name of its own (e.g. an
     * info icon) — otherwise a focusable target is announced with no name at all.
     *
     * Applies the name only, not an ARIA role — see HBPopover's `targetAriaLabel`
     * for why, and for the announcement caveat on roleless elements.
     */
    targetAriaLabel?: string;
    /** The element that triggers the tooltip; can be a render function receiving the current open state. */
    target: ReactNode | ((isOpen: boolean) => JSX.Element);
    /** Additional class name applied to the wrapper element around the target. */
    targetContainerClassName?: string;
    /** Inline styles applied to the wrapper element around the target. */
    targetContainerStyle?: React.CSSProperties;
    /** Visual theme controlling background color and optional icon ('adviceTooltip' adds lightbulb, 'upgradeTooltip' adds diamond). */
    tooltipTheme?: TTooltipTheme;
    /** When true, the tooltip opens when the target receives keyboard focus. */
    openOnFocus?: boolean;
    /** When true, the tooltip renders in its open state on initial mount. */
    enableInitialOpen?: boolean;
}

const HBTooltip = ({
    autoDismissMs,
    children,
    className,
    customStyle,
    delayShowInMs,
    keepOpenOnHover = false,
    disabled,
    forceClose = false,
    forceOpen = false,
    maxWidth = '300px',
    offsetX,
    offsetY,
    onOpenChange = () => {},
    openTrigger = 'hover',
    placement = 'bottom',
    shouldSupportClickOutside = false,
    isTargetContainerFocusable = false,
    targetAriaLabel,
    target,
    targetContainerClassName,
    targetContainerStyle,
    tooltipTheme = 'darkTooltip',
    openOnFocus = true,
    enableInitialOpen
}: ITooltipProps) => {
    const isAdviceTooltip = tooltipTheme === 'adviceTooltip';
    const isUpgradeTooltip = tooltipTheme === 'upgradeTooltip';
    const timeoutRef = useRef(null);
    const [isDoneShowing, setIsDoneShowing] = useState(false);

    useEffect(() => {
        if (!isDoneShowing && !forceClose && autoDismissMs) {
            timeoutRef.current = setTimeout(() => {
                setIsDoneShowing(true);
            }, autoDismissMs);
            return () => {
                clearTimeout(timeoutRef.current);
            };
        }
    }, [isDoneShowing, forceClose, autoDismissMs]);

    if (disabled) {
        // Resolve a render-prop target the same way HBPopover's main path does
        // (target(isOpen)) — the tooltip never opens here, so isOpen is false.
        const resolvedTarget =
            typeof target === 'function' ? target(false) : target;
        return <>{resolvedTarget}</>;
    }

    return (
        <StyledPopover
            className={className}
            customStyle={customStyle}
            delayShowInMs={delayShowInMs}
            keepOpenOnHover={keepOpenOnHover}
            forceClose={forceClose || isDoneShowing}
            forceOpen={forceOpen}
            maxWidth={maxWidth}
            offsetX={offsetX}
            offsetY={offsetY}
            onOpenChange={onOpenChange}
            openTrigger={autoDismissMs ? null : openTrigger}
            placement={placement}
            shouldSupportClickOutside={shouldSupportClickOutside}
            isTargetContainerFocusable={isTargetContainerFocusable}
            targetAriaLabel={targetAriaLabel}
            shouldSupportOverflowHidden
            target={target}
            targetContainerClassName={targetContainerClassName}
            targetContainerStyle={targetContainerStyle}
            theme={tooltipTheme}
            openOnFocus={openOnFocus}
            enableInitialOpen={enableInitialOpen}
            withArrow
            testId="HBTooltip"
            role="tooltip"
        >
            {isAdviceTooltip && (
                <LightbulbIcon color={vars.colors.text.invertedPrimary} />
            )}
            {isUpgradeTooltip && (
                <DiamondIcon color={vars.colors.base.upgrade} />
            )}
            {children}
        </StyledPopover>
    );
};

export default HBTooltip;

// -------------------------------- Styles -------------------------------- //
const StyledPopover = styled(HBPopover)<{ maxWidth: string }>`
    ${({ maxWidth }) => `max-width: ${maxWidth};`}
    white-space: pre-line;

    ${hideInPrint}
`;

const LightbulbIcon = styled(Lightbulb24)`
    position: absolute;
    left: 12px;
    top: 12px;
`;
const DiamondIcon = styled(Diamond16)`
    position: absolute;
    left: 12px;
    top: 12px;
`;
